import type { Geolocation } from "@/lib/geolocation"
import { fetchBrowserGeolocationPosition } from "@/lib/geolocation/browser/fetch_position"

type Timeout = { timeout: true }

const isTimeout = (subject: unknown): subject is Timeout => {
  if (subject && typeof subject === `object`) {
    const { timeout } = subject as Record<string, unknown>
    return typeof timeout === `boolean` && timeout
  }

  return false
}

export const fetchBrowserGeolocationState = async ({
  timeout = 7000,
}: {
  timeout?: number
} = {}): Promise<Geolocation.BrowserState> => {
  const browserSupport = `geolocation` in navigator
  const permission: PermissionStatus = await navigator.permissions.query({
    name: `geolocation`,
  })
  let position: GeolocationPosition | undefined
  let positionError: GeolocationPositionError | undefined
  let timedOut = false

  try {
    position = await new Promise((doResolve, doReject) => {
      let concluded = false

      const resolve: typeof doResolve = (...args) => {
        concluded = true
        return doResolve(...args)
      }

      const reject: typeof doReject = (...args) => {
        concluded = true
        return doReject(...args)
      }

      // Sometimes the browser doesn't return anything at all and leaves us hanging.
      // If we don't get a result within 7 seconds, we want to reject and display
      // an error message.
      setTimeout(async () => {
        if (!concluded) {
          console.warn(
            `fetchBrowserGeolocationPosition timed out after ${
              timeout / 1000
            } seconds without a conclusion.`
          )
          reject({ timeout: true })
        }
      }, timeout)

      fetchBrowserGeolocationPosition()
        .then((result) => {
          concluded = true
          resolve(result)
        })
        .catch((error) => {
          reject(error)
        })
    })
  } catch (error) {
    if (isTimeout(error)) {
      timedOut = true
    } else {
      positionError = error as GeolocationPositionError
      timedOut = positionError.code === GeolocationPositionError.TIMEOUT
    }
  }

  return {
    browserSupport,
    permission,
    position,
    positionError,
    timedOut,
  }
}
